import { type DNDData, IconButton, useDraggable } from '@affine/component';
import { ArrowDownSmallIcon, ArrowUpSmallIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  createContext,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  SIDEBAR_ACTION_REVEAL_CLOSE_EVENT,
  SidebarActionReveal,
} from '../action-reveal';
import {
  enableSidebarBrowserDragGuard,
  scrubSidebarBrowserDragTransfer,
} from '../sidebar-browser-drag-guard';
import * as styles from './index.css';
import {
  getSidebarReorderCommitPosition,
  getSidebarReorderOriginalPlacement,
  getSidebarReorderPlacement,
  type SidebarReorderDragSnapshot,
  type SidebarReorderPlacement,
  type SidebarReorderPosition,
  type SidebarReorderRowSnapshot,
} from './reorder-placement';

type SidebarReorderDNDData = DNDData<
  {
    sidebarReorder: {
      key: string;
      label?: string;
      scope: string;
    };
  },
  {
    sidebarReorder: {
      key: string;
      scope: string;
    };
  }
>;

type SidebarReorderPreview = {
  cursorOffsetX: number;
  cursorOffsetY: number;
  height: number;
  html: string;
  key: string;
  paddingLeft: number;
  paddingRight: number;
  scope: string;
  width: number;
};

type SidebarReorderCursorPreview = {
  clientX: number;
  clientY: number;
  preview: SidebarReorderPreview;
};

type SidebarReorderDragSession = {
  initialScrollTop: number;
  preview: SidebarReorderPreview;
  scope: string;
  scrollElement: HTMLElement | null;
  snapshot: SidebarReorderDragSnapshot;
  sourceKey: string;
};

type SidebarReorderContextValue = {
  activePlacement: SidebarReorderPlacement | null;
  activeDragPreview: SidebarReorderPreview | null;
  activeCursorPreview: SidebarReorderCursorPreview | null;
  activeScope: string | null;
  finishDrag: (
    sourceKey: string,
    scope: string
  ) => SidebarReorderPosition | null;
  startDrag: (options: {
    pointerX: number;
    pointerY: number;
    preview: SidebarReorderPreview;
    scope: string;
    sourceKey: string;
  }) => void;
  updateDragPointer: (options: {
    pointerX: number;
    pointerY: number;
    scope: string;
    sourceKey: string;
  }) => void;
};

const SidebarReorderContext = createContext<SidebarReorderContextValue | null>(
  null
);

const REORDER_ROW_MOTION_MS = 160;
const REORDER_ROW_MOTION_CURVE = 'cubic-bezier(0.2, 0, 0, 1)';
const REORDER_AUTO_SCROLL_MAX_EDGE_PX = 44;
const REORDER_AUTO_SCROLL_MIN_EDGE_PX = 12;
const REORDER_AUTO_SCROLL_EDGE_RATIO = 0.25;
const REORDER_AUTO_SCROLL_MAX_PX_PER_FRAME = 3;

type SidebarReorderRowRect = {
  height: number;
  left: number;
  top: number;
  width: number;
};

const sameSidebarReorderPlacement = (
  a: SidebarReorderPlacement | null,
  b: SidebarReorderPlacement | null
) =>
  a?.at === b?.at &&
  a?.insertionIndex === b?.insertionIndex &&
  a?.isOriginalSlot === b?.isOriginalSlot &&
  a?.key === b?.key &&
  a?.scope === b?.scope &&
  a?.sourceKey === b?.sourceKey;

const getPrefersReducedReorderMotion = () =>
  typeof window === 'undefined'
    ? false
    : (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ??
      false);

const measureSidebarReorderRows = (element: HTMLElement) => {
  const rects = new Map<string, SidebarReorderRowRect>();
  const rows = Array.from(
    element.querySelectorAll<HTMLElement>('[data-sidebar-reorder-key]')
  );

  for (const row of rows) {
    const key = row.dataset.sidebarReorderKey;
    if (!key || row.dataset.dragging === 'true') {
      continue;
    }
    rects.set(key, {
      height: row.offsetHeight,
      left: row.offsetLeft,
      top: row.offsetTop,
      width: row.offsetWidth,
    });
  }

  return rects;
};

const findSidebarReorderScrollElement = (element: HTMLElement) => {
  let current = element.parentElement;

  while (current && current !== document.body) {
    const styles = window.getComputedStyle(current);
    if (
      current.scrollHeight > current.clientHeight + 1 &&
      /(auto|scroll|overlay)/.test(styles.overflowY)
    ) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

const readWithSidebarReorderSourceVisible = <T,>(
  sourceElement: HTMLElement,
  read: () => T
) => {
  const previousDraggingValue = sourceElement.dataset.dragging;
  const hadDraggingValue = previousDraggingValue !== undefined;

  if (hadDraggingValue) {
    delete sourceElement.dataset.dragging;
  }

  try {
    return read();
  } finally {
    if (hadDraggingValue) {
      sourceElement.dataset.dragging = previousDraggingValue;
    }
  }
};

const getSidebarReorderAutoScrollEdgePx = (viewportHeight: number) => {
  const nonOverlappingCap = Math.floor(viewportHeight / 3);
  if (nonOverlappingCap <= 0) {
    return 0;
  }

  const proportionalEdge = Math.max(
    REORDER_AUTO_SCROLL_MIN_EDGE_PX,
    Math.floor(viewportHeight * REORDER_AUTO_SCROLL_EDGE_RATIO)
  );

  return Math.min(
    REORDER_AUTO_SCROLL_MAX_EDGE_PX,
    proportionalEdge,
    nonOverlappingCap
  );
};

const getSidebarReorderAutoScrollVelocity = (
  pointerY: number,
  scrollElement: HTMLElement
) => {
  const rect = scrollElement.getBoundingClientRect();
  const edgePx = getSidebarReorderAutoScrollEdgePx(rect.height);
  if (edgePx <= 0) {
    return 0;
  }

  const topDistance = pointerY - rect.top;
  const bottomDistance = rect.bottom - pointerY;

  if (topDistance < edgePx) {
    const intensity = (edgePx - Math.max(0, topDistance)) / edgePx;
    return -Math.max(
      1,
      Math.ceil(intensity * REORDER_AUTO_SCROLL_MAX_PX_PER_FRAME)
    );
  }

  if (bottomDistance < edgePx) {
    const intensity = (edgePx - Math.max(0, bottomDistance)) / edgePx;
    return Math.max(
      1,
      Math.ceil(intensity * REORDER_AUTO_SCROLL_MAX_PX_PER_FRAME)
    );
  }

  return 0;
};

const snapshotSidebarReorderRows = ({
  listElement,
  sourceKey,
}: {
  listElement: HTMLElement;
  sourceKey: string;
}): SidebarReorderDragSnapshot | null => {
  const rows = Array.from(
    listElement.querySelectorAll<HTMLElement>('[data-sidebar-reorder-key]')
  );
  const sourceIndex = rows.findIndex(
    row => row.dataset.sidebarReorderKey === sourceKey
  );
  if (sourceIndex < 0) {
    return null;
  }

  const targetRows = rows.flatMap((row): SidebarReorderRowSnapshot[] => {
    const key = row.dataset.sidebarReorderKey;
    if (!key || key === sourceKey) {
      return [];
    }

    const rect = row.getBoundingClientRect();
    const top = rect.top;
    const bottom = rect.bottom;
    return [
      {
        bottom,
        key,
        midpoint: top + (bottom - top) / 2,
        top,
      },
    ];
  });

  return {
    sourceIndex,
    sourceKey,
    targetRows,
  };
};

const measureSidebarReorderDragSource = ({
  contentElement,
  sourceElement,
}: {
  contentElement: HTMLElement | null;
  sourceElement: HTMLElement;
}) => {
  return readWithSidebarReorderSourceVisible(sourceElement, () => {
    const previewRect = contentElement?.getBoundingClientRect();
    const listRect = (contentElement ?? sourceElement)
      .closest<HTMLElement>('[data-reorder-scope]')
      ?.getBoundingClientRect();
    const sourceRect = sourceElement.getBoundingClientRect();

    return {
      listRect,
      previewRect,
      sourceRect,
    };
  });
};

const closeSidebarReorderActionReveals = (element: HTMLElement | null) => {
  if (!element) return;

  for (const actionReveal of element.querySelectorAll<HTMLElement>(
    '[data-has-actions="true"]'
  )) {
    actionReveal.dispatchEvent(
      new CustomEvent(SIDEBAR_ACTION_REVEAL_CLOSE_EVENT)
    );
  }
};

const SidebarReorderCursorPreviewLayer = ({
  activePreview,
}: {
  activePreview: SidebarReorderCursorPreview | null;
}) => {
  const renderPreview = (
    cursorPreview: SidebarReorderCursorPreview,
    phase: 'dragging'
  ) => {
    const { preview } = cursorPreview;
    const style = {
      '--streamlain-sidebar-reorder-cursor-left': `${cursorPreview.clientX - preview.cursorOffsetX}px`,
      '--streamlain-sidebar-reorder-cursor-top': `${cursorPreview.clientY - preview.cursorOffsetY}px`,
      '--streamlain-sidebar-reorder-preview-padding-left': `${preview.paddingLeft}px`,
      '--streamlain-sidebar-reorder-preview-padding-right': `${preview.paddingRight}px`,
      '--streamlain-sidebar-reorder-preview-width': `${preview.width}px`,
    } as CSSProperties;

    return (
      <div
        aria-hidden="true"
        className={styles.cursorPreview}
        data-phase={phase}
        key={`${phase}:${preview.scope}:${preview.key}`}
        style={style}
      >
        <div
          className={styles.dragPreview}
          dangerouslySetInnerHTML={{ __html: preview.html }}
        />
      </div>
    );
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>{activePreview ? renderPreview(activePreview, 'dragging') : null}</>,
    document.body
  );
};

export const SidebarReorderableList = forwardRef(
  function SidebarReorderableList(
    {
      children,
      className,
      scope,
    }: {
      children: ReactNode;
      className?: string;
      scope: string;
    },
    ref: ForwardedRef<HTMLDivElement>
  ) {
    const [activePlacement, setActivePlacement] =
      useState<SidebarReorderPlacement | null>(null);
    const [activeDragPreview, setActiveDragPreview] =
      useState<SidebarReorderPreview | null>(null);
    const [activeCursorPreview, setActiveCursorPreview] =
      useState<SidebarReorderCursorPreview | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const dragSessionRef = useRef<SidebarReorderDragSession | null>(null);
    const activeCursorPreviewRef = useRef<SidebarReorderCursorPreview | null>(
      null
    );
    const renderedPlacementRef = useRef<SidebarReorderPlacement | null>(null);
    const previousRowRectsRef = useRef<Map<string, SidebarReorderRowRect>>(
      new Map()
    );
    const motionFrameRef = useRef<number | null>(null);
    const motionTimeoutRef = useRef<number | null>(null);
    const autoScrollFrameRef = useRef<number | null>(null);
    const autoScrollStateRef = useRef<{
      element: HTMLElement;
      velocity: number;
    } | null>(null);
    const animatedRowsRef = useRef<HTMLElement[]>([]);
    const activeScope = activeDragPreview?.scope ?? null;
    const motionActive =
      activeScope === scope || activePlacement?.scope === scope;
    const setActivePlacementIfChanged = useCallback(
      (placement: SidebarReorderPlacement | null) => {
        setActivePlacement(current =>
          sameSidebarReorderPlacement(current, placement) ? current : placement
        );
      },
      []
    );
    const setActiveCursorPreviewState = useCallback(
      (preview: SidebarReorderCursorPreview | null) => {
        activeCursorPreviewRef.current = preview;
        setActiveCursorPreview(preview);
      },
      []
    );
    useLayoutEffect(() => {
      renderedPlacementRef.current = activePlacement;
    }, [activePlacement]);
    const stopAutoScroll = useCallback(() => {
      if (autoScrollFrameRef.current !== null) {
        cancelAnimationFrame(autoScrollFrameRef.current);
        autoScrollFrameRef.current = null;
      }
      autoScrollStateRef.current = null;
    }, []);
    const applyPlacementFromPointer = useCallback(
      (pointerY: number) => {
        const session = dragSessionRef.current;
        if (!session) {
          return;
        }

        const scrollDelta = session.scrollElement
          ? session.scrollElement.scrollTop - session.initialScrollTop
          : 0;
        const placement = getSidebarReorderPlacement({
          pointerY: pointerY + scrollDelta,
          scope: session.scope,
          snapshot: session.snapshot,
        });
        if (placement !== undefined) {
          setActivePlacementIfChanged(placement);
        }
      },
      [setActivePlacementIfChanged]
    );
    const runAutoScroll = useCallback(function runSidebarReorderAutoScroll() {
      const state = autoScrollStateRef.current;
      if (!state || state.velocity === 0) {
        autoScrollFrameRef.current = null;
        return;
      }

      const previousScrollTop = state.element.scrollTop;
      state.element.scrollTop += state.velocity;
      if (state.element.scrollTop === previousScrollTop) {
        autoScrollFrameRef.current = null;
        autoScrollStateRef.current = null;
        return;
      }
      autoScrollFrameRef.current = requestAnimationFrame(
        runSidebarReorderAutoScroll
      );
    }, []);
    const updateAutoScroll = useCallback(
      (pointerY: number, scrollElement: HTMLElement | null) => {
        if (!scrollElement) {
          stopAutoScroll();
          return;
        }

        const velocity = getSidebarReorderAutoScrollVelocity(
          pointerY,
          scrollElement
        );
        if (velocity === 0) {
          stopAutoScroll();
          return;
        }

        autoScrollStateRef.current = {
          element: scrollElement,
          velocity,
        };
        if (autoScrollFrameRef.current === null) {
          autoScrollFrameRef.current = requestAnimationFrame(runAutoScroll);
        }
      },
      [runAutoScroll, stopAutoScroll]
    );
    const startDrag = useCallback(
      ({
        pointerX,
        pointerY,
        preview,
        scope: dragScope,
        sourceKey,
      }: {
        pointerX: number;
        pointerY: number;
        preview: SidebarReorderPreview;
        scope: string;
        sourceKey: string;
      }) => {
        const listElement = listRef.current;
        const scrollElement = listElement
          ? findSidebarReorderScrollElement(listElement)
          : null;
        const snapshot = listElement
          ? snapshotSidebarReorderRows({
              listElement,
              sourceKey,
            })
          : null;

        closeSidebarReorderActionReveals(listElement);
        dragSessionRef.current = snapshot
          ? {
              initialScrollTop: scrollElement?.scrollTop ?? 0,
              preview,
              scope: dragScope,
              scrollElement,
              snapshot,
              sourceKey,
            }
          : null;
        setActivePlacementIfChanged(
          snapshot
            ? getSidebarReorderOriginalPlacement({
                scope: dragScope,
                snapshot,
              })
            : null
        );
        setActiveDragPreview(preview);
        setActiveCursorPreviewState({
          clientX: pointerX,
          clientY: pointerY,
          preview,
        });
      },
      [setActiveCursorPreviewState, setActivePlacementIfChanged]
    );
    const updateDragPointer = useCallback(
      ({
        pointerX,
        pointerY,
        scope: dragScope,
        sourceKey,
      }: {
        pointerX: number;
        pointerY: number;
        scope: string;
        sourceKey: string;
      }) => {
        const session = dragSessionRef.current;
        if (
          !session ||
          session.scope !== dragScope ||
          session.sourceKey !== sourceKey
        ) {
          return;
        }

        updateAutoScroll(pointerY, session.scrollElement);
        applyPlacementFromPointer(pointerY);
        setActiveCursorPreviewState({
          clientX: pointerX,
          clientY: pointerY,
          preview: session.preview,
        });
      },
      [applyPlacementFromPointer, setActiveCursorPreviewState, updateAutoScroll]
    );
    const finishDrag = useCallback(
      (sourceKey: string, dragScope: string) => {
        const session = dragSessionRef.current;
        const placement = renderedPlacementRef.current;
        const commitPosition = getSidebarReorderCommitPosition(
          session &&
            session.scope === dragScope &&
            session.sourceKey === sourceKey &&
            placement?.scope === dragScope
            ? placement
            : null
        );

        stopAutoScroll();
        closeSidebarReorderActionReveals(listRef.current);
        dragSessionRef.current = null;
        setActivePlacementIfChanged(null);
        setActiveDragPreview(null);
        setActiveCursorPreviewState(null);

        return commitPosition;
      },
      [setActiveCursorPreviewState, setActivePlacementIfChanged, stopAutoScroll]
    );
    const value = useMemo(
      () => ({
        activePlacement,
        activeCursorPreview,
        activeDragPreview,
        activeScope,
        finishDrag,
        startDrag,
        updateDragPointer,
      }),
      [
        activePlacement,
        activeCursorPreview,
        activeDragPreview,
        activeScope,
        finishDrag,
        startDrag,
        updateDragPointer,
      ]
    );
    const setListRef = useCallback(
      (node: HTMLDivElement | null) => {
        listRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    useLayoutEffect(() => {
      const listElement = listRef.current;
      if (!listElement) {
        previousRowRectsRef.current = new Map();
        return;
      }

      if (motionFrameRef.current !== null) {
        cancelAnimationFrame(motionFrameRef.current);
        motionFrameRef.current = null;
      }
      if (motionTimeoutRef.current !== null) {
        window.clearTimeout(motionTimeoutRef.current);
        motionTimeoutRef.current = null;
      }
      for (const row of animatedRowsRef.current) {
        row.style.transition = '';
        row.style.transform = '';
        row.style.willChange = '';
        delete row.dataset.reorderAnimating;
      }
      animatedRowsRef.current = [];

      const previousRects = previousRowRectsRef.current;
      const currentRects = measureSidebarReorderRows(listElement);
      previousRowRectsRef.current = currentRects;

      if (
        !motionActive ||
        previousRects.size === 0 ||
        getPrefersReducedReorderMotion()
      ) {
        return;
      }

      const animatedRows: HTMLElement[] = [];
      const rows = Array.from(
        listElement.querySelectorAll<HTMLElement>('[data-sidebar-reorder-key]')
      );

      for (const row of rows) {
        const key = row.dataset.sidebarReorderKey;
        if (!key || row.dataset.dragging === 'true') {
          continue;
        }

        const previousRect = previousRects.get(key);
        const currentRect = currentRects.get(key);
        if (!previousRect || !currentRect) {
          continue;
        }

        const translateX = previousRect.left - currentRect.left;
        const translateY = previousRect.top - currentRect.top;
        if (Math.abs(translateX) < 0.5 && Math.abs(translateY) < 0.5) {
          continue;
        }

        row.dataset.reorderAnimating = 'true';
        row.style.transition = 'none';
        row.style.transform = `translate(${translateX}px, ${translateY}px)`;
        row.style.willChange = 'transform';
        animatedRows.push(row);
      }

      if (animatedRows.length === 0) {
        return;
      }

      animatedRowsRef.current = animatedRows;
      motionFrameRef.current = requestAnimationFrame(() => {
        for (const row of animatedRows) {
          row.style.transition = `transform ${REORDER_ROW_MOTION_MS}ms ${REORDER_ROW_MOTION_CURVE}`;
          row.style.transform = '';
        }
        motionFrameRef.current = null;
      });
      motionTimeoutRef.current = window.setTimeout(() => {
        for (const row of animatedRows) {
          row.style.transition = '';
          row.style.transform = '';
          row.style.willChange = '';
          delete row.dataset.reorderAnimating;
        }
        animatedRowsRef.current = [];
        motionTimeoutRef.current = null;
      }, REORDER_ROW_MOTION_MS + 40);
    });

    useEffect(
      () => () => {
        if (motionFrameRef.current !== null) {
          cancelAnimationFrame(motionFrameRef.current);
        }
        if (motionTimeoutRef.current !== null) {
          window.clearTimeout(motionTimeoutRef.current);
        }
        if (autoScrollFrameRef.current !== null) {
          cancelAnimationFrame(autoScrollFrameRef.current);
        }
        autoScrollStateRef.current = null;
      },
      []
    );

    return (
      <SidebarReorderContext.Provider value={value}>
        <SidebarReorderCursorPreviewLayer activePreview={activeCursorPreview} />
        <div
          ref={setListRef}
          className={clsx(styles.list, className)}
          data-reorder-active={activeScope === scope ? 'true' : undefined}
          data-reorder-scope={scope}
        >
          {children}
        </div>
      </SidebarReorderContext.Provider>
    );
  }
);

const SidebarReorderHandle = ({
  canMoveDown,
  canMoveUp,
  onMoveDown,
  onMoveUp,
  testIdPrefix,
}: {
  canMoveDown: boolean;
  canMoveUp: boolean;
  onMoveDown?: () => void;
  onMoveUp?: () => void;
  testIdPrefix: string;
}) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp' && canMoveUp) {
        event.preventDefault();
        onMoveUp?.();
      } else if (event.key === 'ArrowDown' && canMoveDown) {
        event.preventDefault();
        onMoveDown?.();
      }
    },
    [canMoveDown, canMoveUp, onMoveDown, onMoveUp]
  );

  return (
    <IconButton
      aria-label="Element verschieben"
      data-testid={`${testIdPrefix}-reorder-handle`}
      disabled={!canMoveUp && !canMoveDown}
      onKeyDown={handleKeyDown}
      size="16"
    >
      <span className={styles.handleIconStack}>
        <ArrowUpSmallIcon data-testid={`${testIdPrefix}-move-up-button`} />
        <ArrowDownSmallIcon data-testid={`${testIdPrefix}-move-down-button`} />
      </span>
    </IconButton>
  );
};

export type SidebarReorderableItemProps = {
  canMoveDown: boolean;
  canMoveUp: boolean;
  children:
    | ReactElement
    | ((actions?: ReactElement) => ReactElement | null)
    | null;
  className?: string;
  itemKey: string;
  label?: string;
  onMoveDown?: () => void;
  onMoveUp?: () => void;
  onPlace: (itemKey: string, position: SidebarReorderPosition) => void;
  scope: string;
  testIdPrefix: string;
};

export const SidebarReorderableItem = ({
  canMoveDown,
  canMoveUp,
  children,
  className,
  itemKey,
  label,
  onMoveDown,
  onMoveUp,
  onPlace,
  scope,
  testIdPrefix,
}: SidebarReorderableItemProps) => {
  const reorderContext = useContext(SidebarReorderContext);
  const activePlacement = reorderContext?.activePlacement;
  const activeDragPreview = reorderContext?.activeDragPreview;
  const finishDrag = reorderContext?.finishDrag;
  const startDrag = reorderContext?.startDrag;
  const updateDragPointer = reorderContext?.updateDragPointer;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const browserSidebarDragGuardCleanupRef = useRef<(() => void) | null>(null);
  const reorderable = canMoveUp || canMoveDown;
  const disableBrowserSidebarDragGuard = useCallback(() => {
    browserSidebarDragGuardCleanupRef.current?.();
    browserSidebarDragGuardCleanupRef.current = null;
  }, []);
  const enableBrowserSidebarDragGuard = useCallback(() => {
    if (browserSidebarDragGuardCleanupRef.current) {
      return;
    }
    browserSidebarDragGuardCleanupRef.current = enableSidebarBrowserDragGuard();
  }, []);
  const handleBrowserSidebarDragStartCapture = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      if (!reorderable) {
        return;
      }
      scrubSidebarBrowserDragTransfer(event.dataTransfer);
      enableBrowserSidebarDragGuard();
    },
    [enableBrowserSidebarDragGuard, reorderable]
  );
  useEffect(
    () => disableBrowserSidebarDragGuard,
    [disableBrowserSidebarDragGuard]
  );
  const { dragRef } = useDraggable<SidebarReorderDNDData>(
    () => ({
      canDrag: reorderable,
      data: {
        sidebarReorder: {
          key: itemKey,
          label,
          scope,
        },
      },
      disableDragPreview: true,
      toExternalData: () => ({}),
      onDragStart: data => {
        enableBrowserSidebarDragGuard();
        const sourceElement = data.source.element;
        closeSidebarReorderActionReveals(sourceElement);
        const { listRect, previewRect, sourceRect } =
          measureSidebarReorderDragSource({
            contentElement: contentRef.current,
            sourceElement,
          });
        const initialInput = data.location.initial.input;
        const previewPaddingLeft =
          previewRect && listRect ? previewRect.left - listRect.left : 0;
        const previewPaddingRight =
          previewRect && listRect ? listRect.right - previewRect.right : 0;
        const cursorPreviewLeft =
          listRect?.left ?? previewRect?.left ?? sourceRect.left;
        const cursorPreviewTop = previewRect?.top ?? sourceRect.top;
        const previewWidth = listRect ? listRect.width : sourceRect.width;
        const preview = {
          cursorOffsetX: initialInput.clientX - cursorPreviewLeft,
          cursorOffsetY: initialInput.clientY - cursorPreviewTop,
          height: Math.max(30, previewRect?.height ?? sourceRect.height ?? 30),
          html: contentRef.current?.innerHTML ?? '',
          key: itemKey,
          paddingLeft: Number.isFinite(previewPaddingLeft)
            ? Math.max(0, previewPaddingLeft)
            : 0,
          paddingRight: Number.isFinite(previewPaddingRight)
            ? Math.max(0, previewPaddingRight)
            : 0,
          scope,
          width: Math.max(1, previewWidth || 1),
        };
        startDrag?.({
          pointerX: initialInput.clientX,
          pointerY: initialInput.clientY,
          preview,
          scope,
          sourceKey: itemKey,
        });
      },
      onDrag: data => {
        updateDragPointer?.({
          pointerX: data.location.current.input.clientX,
          pointerY: data.location.current.input.clientY,
          scope,
          sourceKey: itemKey,
        });
      },
      onDrop: () => {
        const placement = finishDrag?.(itemKey, scope);
        disableBrowserSidebarDragGuard();
        closeSidebarReorderActionReveals(contentRef.current);
        if (placement) {
          onPlace(itemKey, placement);
        }
      },
    }),
    [
      itemKey,
      label,
      onPlace,
      reorderable,
      scope,
      disableBrowserSidebarDragGuard,
      enableBrowserSidebarDragGuard,
      finishDrag,
      startDrag,
      updateDragPointer,
    ]
  );
  const setItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      dragRef.current = node;
    },
    [dragRef]
  );
  const effectivePlacement =
    activePlacement?.scope === scope && activePlacement.key === itemKey
      ? activePlacement
      : null;
  const ghostPreview =
    activeDragPreview?.scope === scope && effectivePlacement
      ? activeDragPreview
      : null;
  const sourceHidden =
    activeDragPreview?.scope === scope &&
    activeDragPreview.key === itemKey &&
    activePlacement?.scope === scope &&
    activePlacement.sourceKey === itemKey;
  const ghostStyle = ghostPreview
    ? ({
        '--streamlain-sidebar-reorder-ghost-height': `${ghostPreview.height}px`,
      } as CSSProperties)
    : undefined;
  const actions = (
    <SidebarReorderHandle
      canMoveDown={canMoveDown}
      canMoveUp={canMoveUp}
      onMoveDown={onMoveDown}
      onMoveUp={onMoveUp}
      testIdPrefix={testIdPrefix}
    />
  );
  const content =
    typeof children === 'function' ? (
      children(actions)
    ) : children ? (
      <SidebarActionReveal className={styles.actionReveal} actions={actions}>
        {children}
      </SidebarActionReveal>
    ) : null;
  if (!content) {
    return null;
  }

  const renderGhost = (at: 'before' | 'after') => {
    if (!ghostPreview || effectivePlacement?.at !== at) {
      return null;
    }

    return (
      <div
        className={styles.ghost}
        data-edge={at === 'before' ? 'top' : 'bottom'}
        style={ghostStyle}
      >
        {ghostPreview.html ? (
          <div
            aria-hidden="true"
            className={styles.ghostPreview}
            dangerouslySetInnerHTML={{ __html: ghostPreview.html }}
          />
        ) : null}
      </div>
    );
  };

  return (
    <>
      {renderGhost('before')}
      <div
        ref={setItemRef}
        className={clsx(styles.root, className)}
        data-reorder-source-hidden={sourceHidden ? 'true' : undefined}
        data-reorderable={reorderable ? 'true' : 'false'}
        data-sidebar-reorder-key={itemKey}
        onDragStartCapture={handleBrowserSidebarDragStartCapture}
      >
        <div ref={contentRef} className={styles.content}>
          {content}
        </div>
      </div>
      {renderGhost('after')}
    </>
  );
};
